import { HeadersFunction, LoaderFunctionArgs, redirect } from '@remix-run/node';
import { MetaArgs, NavLink, useLoaderData } from '@remix-run/react';
import { GetHelpCategoriesDocument, GetHelpCategoryDocument, GetWebsiteRedirectsDocument } from '@theprintbar/graphql';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonDay } from '@theprintbar/server';
import { BrandColours, WebRoutes, cn, invariantNotFound } from '@theprintbar/shared';
import { Accordion, Container, MediumArrowIcon, PromoImageContainer, RichText, SplitCTA } from '@theprintbar/ui';
import { useState } from 'react';
import { SitemapFunction } from 'remix-sitemap';
import { serverOnly$ } from 'vite-env-only/macros';
import { BreadCrumbs } from '~/components/BreadCrumbs';
import type { loader as ContentLoader } from '~/routes/_layout/_index/_index';
import { contentMeta } from '~/utils/contentMeta';
import { getFAQStructuredData } from './utils/getFAQStructuredData';

export const meta = (args: MetaArgs<typeof loader>) => {
	const content = contentMeta(args as MetaArgs<typeof ContentLoader>);
	return [...content, getFAQStructuredData(args.data)];
};

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export const sitemap: SitemapFunction = serverOnly$(async ({}) => {
	const { helpCategories } = await runSudoGraphql(GetHelpCategoriesDocument);

	return (
		helpCategories?.map(({ slug, updatedAt }) => {
			return {
				loc: WebRoutes.HelpCategoryPage(slug),
				lastmod: new Date(updatedAt).toISOString(),
				priority: 0.5,
			};
		}) || []
	);
})!;

export async function loader({ request, params }: LoaderFunctionArgs) {
	const { slug } = params;
	invariantNotFound(slug, 'No slug provided');

	const [{ helpCategory }, { helpCategories }] = await Promise.all([
		runSudoGraphql(GetHelpCategoryDocument, { where: { slug } }),
		runSudoGraphql(GetHelpCategoriesDocument),
	]);

	if (!helpCategory) {
		const { websiteRedirects } = await runSudoGraphql(GetWebsiteRedirectsDocument, {
			where: { from: { equals: `/help/${slug}` } },
		});

		if (websiteRedirects?.[0]?.to) {
			return redirect(`${websiteRedirects[0].to}${new URL(request.url).search}`, 301);
		}
		invariantNotFound(helpCategory, 'Category not found');
	}

	return cachedJsonDay({ contentPage: helpCategory, helpCategories, slug });
}

export default function () {
	const { contentPage, helpCategories, slug } = useLoaderData<typeof loader>();
	const [activeLink, setActiveLink] = useState(slug);

	return (
		<>
			<h1 className="hidden">{contentPage.headingTag || contentPage.metaTitle}</h1>
			<PromoImageContainer className="h-44 lg:h-60 w-full" promoImage={contentPage.heroBanner.structure?.[0]?.image} />
			<Container className="lg:hidden mt-5">
				<h4 className="font-display font-light text-2xl normal-case mb-8 lg:mb-0">
					{contentPage.heroBanner.structure?.[0]?.title}
				</h4>
				<RichText className="space-y-2" {...contentPage.heroBanner.structure?.[0]?.text} />
			</Container>

			<Container className="my-8 lg:my-16 w-full">
				<BreadCrumbs
					pages={[
						{
							name: 'Help Centre',
							href: WebRoutes.HelpListing,
							current: false,
						},
						{
							name: contentPage.heroBanner.structure?.[0]?.title || 'Category',
							href: WebRoutes.HelpCategoryPage(slug),
							current: true,
						},
					]}
				/>

				<div className="flex flex-col lg:gap-32 lg:flex-row mt-5">
					{/* Category lists on the left */}
					<div className="flex flex-row lg:flex-col gap-2.5 lg:gap-5 flex-wrap">
						<h4 className="hidden lg:block font-display font-light text-2xl lg:text-3xl normal-case lg:mb-10 whitespace-nowrap">
							{contentPage.heroBanner.structure?.[0]?.title}
						</h4>
						{helpCategories?.map(({ slug, heroBanner }) => (
							<NavLink key={slug} to={WebRoutes.HelpCategoryPage(slug)} onClick={() => setActiveLink(slug)}>
								<div
									style={{
										backgroundColor:
											activeLink === slug ? heroBanner.structure?.[0]?.colour || BrandColours.Blue : 'transparent',
									}}
									className={cn(
										'rounded-[100px] lg:rounded-[60px] border border-blue py-2.5 px-5 lg:py-3 lg:px-6 flex justify-center items-center text-sm lg:text-xl w-max font-display font-light',
										heroBanner.structure?.[0]?.colour === BrandColours.Blue && activeLink === slug
											? 'text-white'
											: 'text-blue',
									)}
								>
									{heroBanner.structure?.[0]?.title}
								</div>
							</NavLink>
						))}
					</div>

					<div className="flex flex-col w-full h-fit">
						<RichText className="space-y-2 hidden lg:block lg:max-w-xl" {...contentPage.heroBanner.structure?.[0]?.text} />
						<div className="my-9 w-full max-w-4xl">
							{contentPage.articles?.map(({ title, content }) => (
								<Accordion
									key={title}
									title={
										<p className="lg:text-base text-lg font-light lg:font-normal font-display lg:font-sans">{title}</p>
									}
									openIcon={MediumArrowIcon}
									closeIcon={MediumArrowIcon}
									openIconClass="fill-blue w-3 h-3 rotate-180"
									closeIconClass="fill-blue w-3 h-3"
								>
									<RichText className="space-y-2 mt-6 max-w-2xl" content={content} />
								</Accordion>
							))}
						</div>

						<SplitCTA content={contentPage} />
					</div>
				</div>
			</Container>
		</>
	);
}
